import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { creatorApi } from '../data/creator.api';
import type { CreatorIdentity, OnboardingState } from '@shared/web/types';

interface CreatorAuthValue {
  creator: CreatorIdentity | null;
  onboarding: OnboardingState | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True only when the API will actually serve the portal's own routes. */
  isOnboarded: boolean;
  setSession: (creator: CreatorIdentity, onboarding: OnboardingState) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<OnboardingState | null>;
}

const CreatorAuthContext = createContext<CreatorAuthValue | null>(null);

/**
 * Who is signed in, and what they still have to do.
 *
 * ── Why onboarding lives in the auth context ───────────────────────────────
 *
 * It could be its own hook. It is here because the two are read together on
 * every single render of the app shell — "is there a creator, and may they see
 * the portal" is one question with one answer — and splitting them means two
 * loading states, two fetches, and a window where the app knows who you are
 * but not whether to show you the tabs. That window is visible, and it is
 * exactly the flicker this flow was specified to avoid.
 *
 * The server is still the authority. `requireOnboarded` refuses the portal's
 * routes regardless of what this context believes; `isOnboarded` only decides
 * which screen to render, never whether data is safe to serve.
 *
 * Separate from `useAuth` on purpose. A shared provider would mean one
 * `isAuthenticated` covering two different kinds of user, and the first
 * component to read it without checking which kind is a bug you cannot see.
 */
export function CreatorAuthProvider({ children }: { children: ReactNode }) {
  const [creator, setCreator] = useState<CreatorIdentity | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const session = await creatorApi.me();
      setCreator(session.creator);
      setOnboarding(session.onboarding);
      return session.onboarding;
    } catch {
      setCreator(null);
      setOnboarding(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setSession = useCallback((next: CreatorIdentity, state: OnboardingState) => {
    setCreator(next);
    setOnboarding(state);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await creatorApi.logOut();
    } finally {
      setCreator(null);
      setOnboarding(null);
      // A full navigation rather than a router push: it drops every cached
      // fetch and component state belonging to the session that just ended.
      window.location.href = '/creator/login';
    }
  }, []);

  const value = useMemo(
    () => ({
      creator,
      onboarding,
      isLoading,
      isAuthenticated: Boolean(creator),
      isOnboarded: Boolean(creator && onboarding?.isComplete),
      setSession,
      signOut,
      refresh,
    }),
    [creator, onboarding, isLoading, setSession, signOut, refresh],
  );

  return <CreatorAuthContext.Provider value={value}>{children}</CreatorAuthContext.Provider>;
}

export function useCreatorAuth() {
  const context = useContext(CreatorAuthContext);
  if (!context) throw new Error('useCreatorAuth must be used inside <CreatorAuthProvider>');
  return context;
}
